import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { CheckIcon, CreditCardIcon, ArrowLeftIcon } from 'lucide-react';
// Pricing plan details
const pricingPlans = {
  basic: {
    id: 'basic',
    name: 'Basic',
    description: 'Perfect for individuals and small projects',
    monthlyPrice: 19,
    yearlyPrice: 190,
    features: ['Access to 50+ templates', 'Basic customization options', 'Standard support', 'Updates for 6 months', 'Use on 1 project']
  },
  pro: {
    id: 'pro',
    name: 'Professional',
    description: 'Ideal for professionals and growing teams',
    monthlyPrice: 49,
    yearlyPrice: 490,
    features: ['Access to 200+ templates', 'Advanced customization options', 'Priority support', 'Lifetime updates', 'Use on 5 projects', 'Access to premium assets', 'Export in multiple formats']
  },
  enterprise: {
    id: 'enterprise',
    name: 'Enterprise',
    description: 'For large teams and organizations',
    monthlyPrice: 99,
    yearlyPrice: 990,
    features: ['Access to all templates', 'Custom modifications', 'Dedicated support', 'Lifetime updates', 'Unlimited projects', 'Access to all premium assets', 'Custom export formats', 'Team collaboration tools', 'White-label options']
  }
};
export const SubscribePage = () => {
  const {
    planId
  } = useParams<{
    planId: string;
  }>();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [plan, setPlan] = useState<any>(null);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    // Get plan details
    if (planId && pricingPlans[planId as keyof typeof pricingPlans]) {
      setPlan(pricingPlans[planId as keyof typeof pricingPlans]);
    } else {
      // Redirect to pricing if plan doesn't exist
      navigate('/');
    }
  }, [planId, navigate]);
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrolled]);
  const handleBillingCycleChange = (cycle: 'monthly' | 'yearly') => {
    setBillingCycle(cycle);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    // Simulate payment processing
    setTimeout(() => {
      setLoading(false);
      navigate(`/checkout/confirmation/plan/${planId}`);
    }, 1500);
  };
  if (!plan) {
    return <div className="w-full bg-gray-50 dark:bg-[#0A1C1A] min-h-screen" data-id="element-6904">
        <Navbar scrolled={scrolled} data-id="element-6905" />
        <main className="pt-24 pb-20" data-id="element-6906">
          <div className="container mx-auto px-4 md:px-6" data-id="element-6907">
            <div className="max-w-3xl mx-auto text-center" data-id="element-6908">
              <h1 className="text-2xl font-bold text-gray-800 dark:text-[#F8FAF9]" data-id="element-6909">
                Loading...
              </h1>
            </div>
          </div>
        </main>
        <Footer data-id="element-6910" />
      </div>;
  }
  const price = billingCycle === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice;
  return <div className="w-full bg-gray-50 dark:bg-[#0A1C1A] min-h-screen" data-id="element-6911">
      <Navbar scrolled={scrolled} data-id="element-6912" />
      <main className="pt-24 pb-20" data-id="element-6913">
        <div className="container mx-auto px-4 md:px-6" data-id="element-6914">
          <div className="max-w-3xl mx-auto" data-id="element-6915">
            {/* Back button */}
            <button onClick={() => navigate(-1)} className="inline-flex items-center text-gray-600 dark:text-[#B0C1BD] hover:text-[#2DB489] dark:hover:text-[#4ED1A0] mb-6" data-id="element-6916">
              <ArrowLeftIcon className="h-5 w-5 mr-1" data-id="element-6917" />
              Back to Pricing
            </button>
            <div className="bg-white dark:bg-[#162825] rounded-xl shadow-sm overflow-hidden" data-id="element-6918">
              <div className="bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] px-6 py-8 text-white" data-id="element-6919">
                <h1 className="text-2xl font-bold mb-2" data-id="element-6920">
                  Subscribe to {plan.name} Plan
                </h1>
                <p data-id="element-6921">{plan.description}</p>
              </div>
              <div className="p-6 md:p-8" data-id="element-6922">
                {/* Plan details */}
                <div className="mb-8" data-id="element-6923">
                  <div className="flex justify-between items-center mb-6" data-id="element-6924">
                    <h2 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9]" data-id="element-6925">
                      Plan Details
                    </h2>
                    <div className="flex items-center bg-gray-100 dark:bg-[#1A3330] rounded-full p-1" data-id="element-6926">
                      <button className={`px-3 py-1 rounded-full text-sm ${billingCycle === 'monthly' ? 'bg-white dark:bg-[#162825] shadow-sm' : ''}`} onClick={() => handleBillingCycleChange('monthly')} data-id="element-6927">
                        Monthly
                      </button>
                      <button className={`px-3 py-1 rounded-full text-sm ${billingCycle === 'yearly' ? 'bg-white dark:bg-[#162825] shadow-sm' : ''}`} onClick={() => handleBillingCycleChange('yearly')} data-id="element-6928">
                        Yearly
                      </button>
                    </div>
                  </div>
                  <div className="bg-gray-50 dark:bg-[#1A3330]/50 rounded-lg p-4 mb-6" data-id="element-6929">
                    <div className="flex justify-between items-center mb-2" data-id="element-6930">
                      <span className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-6931">
                        {plan.name} Plan ({billingCycle})
                      </span>
                      <span className="font-semibold text-gray-800 dark:text-[#F8FAF9]" data-id="element-6932">
                        ${price}
                      </span>
                    </div>
                    {billingCycle === 'yearly' && <div className="text-sm text-green-600 dark:text-green-400" data-id="element-6933">
                        Save ${plan.monthlyPrice * 12 - plan.yearlyPrice} with
                        annual billing
                      </div>}
                  </div>
                  <h3 className="font-semibold text-gray-800 dark:text-[#F8FAF9] mb-3" data-id="element-6934">
                    What's included:
                  </h3>
                  <ul className="space-y-2 mb-6" data-id="element-6935">
                    {plan.features.map((feature: string, index: number) => <li key={index} className="flex items-start" data-id="element-6936">
                        <CheckIcon className="h-5 w-5 text-[#2DB489] dark:text-[#4ED1A0] flex-shrink-0 mr-2" data-id="element-6937" />
                        <span className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-6938">
                          {feature}
                        </span>
                      </li>)}
                  </ul>
                </div>
                {/* Payment form */}
                <form onSubmit={handleSubmit} data-id="element-6939">
                  <h2 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-6" data-id="element-6940">
                    Payment Information
                  </h2>
                  <div className="mb-6" data-id="element-6941">
                    <label className="block text-gray-700 dark:text-[#B0C1BD] mb-2 text-sm font-medium" data-id="element-6942">
                      Card Information
                    </label>
                    <div className="relative" data-id="element-6943">
                      <input type="text" placeholder="Card number" className="w-full px-4 py-3 border border-gray-300 dark:border-[#1A3330] rounded-lg bg-white dark:bg-[#1A3330]/50 text-gray-800 dark:text-[#F8FAF9] focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] pr-10" required data-id="element-6944" />
                      <CreditCardIcon className="absolute right-3 top-3 h-5 w-5 text-gray-400 dark:text-[#7A8F8A]" data-id="element-6945" />
                    </div>
                    <div className="grid grid-cols-2 gap-4 mt-4" data-id="element-6946">
                      <input type="text" placeholder="MM/YY" className="px-4 py-3 border border-gray-300 dark:border-[#1A3330] rounded-lg bg-white dark:bg-[#1A3330]/50 text-gray-800 dark:text-[#F8FAF9] focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0]" required data-id="element-6947" />
                      <input type="text" placeholder="CVC" className="px-4 py-3 border border-gray-300 dark:border-[#1A3330] rounded-lg bg-white dark:bg-[#1A3330]/50 text-gray-800 dark:text-[#F8FAF9] focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0]" required data-id="element-6948" />
                    </div>
                  </div>
                  <div className="mb-6" data-id="element-6949">
                    <label className="block text-gray-700 dark:text-[#B0C1BD] mb-2 text-sm font-medium" data-id="element-6950">
                      Name on Card
                    </label>
                    <input type="text" placeholder="Full name" className="w-full px-4 py-3 border border-gray-300 dark:border-[#1A3330] rounded-lg bg-white dark:bg-[#1A3330]/50 text-gray-800 dark:text-[#F8FAF9] focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0]" required data-id="element-6951" />
                  </div>
                  <div className="mb-8" data-id="element-6952">
                    <label className="block text-gray-700 dark:text-[#B0C1BD] mb-2 text-sm font-medium" data-id="element-6953">
                      Billing Email
                    </label>
                    <input type="email" placeholder="Email address" className="w-full px-4 py-3 border border-gray-300 dark:border-[#1A3330] rounded-lg bg-white dark:bg-[#1A3330]/50 text-gray-800 dark:text-[#F8FAF9] focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0]" required data-id="element-6954" />
                  </div>
                  <div className="border-t border-gray-200 dark:border-[#1A3330] pt-6 mb-6" data-id="element-6955">
                    <div className="flex justify-between mb-2" data-id="element-6956">
                      <span className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-6957">
                        Subtotal
                      </span>
                      <span className="text-gray-800 dark:text-[#F8FAF9]" data-id="element-6958">
                        ${price}
                      </span>
                    </div>
                    <div className="flex justify-between mb-2" data-id="element-6959">
                      <span className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-6960">
                        Tax
                      </span>
                      <span className="text-gray-800 dark:text-[#F8FAF9]" data-id="element-6961">
                        $0
                      </span>
                    </div>
                    <div className="flex justify-between font-semibold text-lg" data-id="element-6962">
                      <span className="text-gray-800 dark:text-[#F8FAF9]" data-id="element-6963">
                        Total
                      </span>
                      <span className="text-gray-800 dark:text-[#F8FAF9]" data-id="element-6964">
                        ${price}
                      </span>
                    </div>
                  </div>
                  <button type="submit" className={`w-full bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] text-white py-3 rounded-lg font-medium hover:shadow-lg transition-all ${loading ? 'opacity-70 cursor-not-allowed' : ''}`} disabled={loading} data-id="element-6965">
                    {loading ? 'Processing...' : `Subscribe for $${price}`}
                  </button>
                  <p className="text-sm text-gray-500 dark:text-[#7A8F8A] mt-4 text-center" data-id="element-6966">
                    By subscribing, you agree to our{' '}
                    <a href="/terms" className="text-[#2DB489] dark:text-[#4ED1A0] hover:underline" data-id="element-6967">
                      Terms of Service
                    </a>{' '}
                    and{' '}
                    <a href="/privacy-policy" className="text-[#2DB489] dark:text-[#4ED1A0] hover:underline" data-id="element-6968">
                      Privacy Policy
                    </a>
                    .
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer data-id="element-6969" />
    </div>;
};